import { A, useLocation } from "@solidjs/router";
import { Show } from "solid-js";
import { announcement } from "virtual:changelog-announcement";
import { IOS_APP_URL } from "~/components/marketing/content";
import { DownloadButton, GetStartedButton } from "~/components/marketing/CTAButtons";
import { useI18n } from "~/i18n";
import type { TranslationKey } from "~/i18n/translations";
import { hasChiefBuild } from "~/lib/chiefDownload";
import { useOS } from "~/lib/useOS";
import { HeroProof } from "./HeroProof";
import { HeroVisual } from "./HeroVisual";

// /?landing=<audience> (or /?s=<audience>) speaks to that audience in the hero and points on to its page.
const AUDIENCES: Record<string, { headline: TranslationKey; sub: TranslationKey; page: string; link: TranslationKey }> =
  {
    builders: {
      headline: "marketing.builders.hero.title",
      sub: "marketing.builders.hero.sub",
      page: "/for-builders",
      link: "marketing.nav.for_builders",
    },
    executives: {
      headline: "marketing.executives.hero.title",
      sub: "marketing.executives.hero.sub",
      page: "/for-executives",
      link: "marketing.nav.for_executives",
    },
  };

export const HeroSection = () => {
  const { t } = useI18n();
  const os = useOS();
  const location = useLocation();
  const audience = () => {
    const key = String(location.query.landing ?? location.query.s);
    return Object.hasOwn(AUDIENCES, key) ? AUDIENCES[key] : undefined;
  };
  const headline = () => t(audience()?.headline ?? "marketing.home.hero.headline").split("\n");

  return (
    <section class="relative overflow-hidden px-4 pt-12 pb-16 sm:px-6 sm:pt-20 sm:pb-24 lg:px-8">
      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 bg-[radial-gradient(var(--color-primary-border)_1px,transparent_1px)] mask-[radial-gradient(ellipse_at_top,black_30%,transparent_75%)] bg-size-[24px_24px]"
      />
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -top-40 left-1/2 h-112 w-240 -translate-x-1/2 rounded-full bg-brand/10 blur-3xl"
      />

      <div class="relative mx-auto max-w-6xl text-center">
        <Show
          when={announcement}
          fallback={
            <A
              href="/compare"
              class="group inline-flex items-center gap-2 rounded-full border border-primary-border bg-tile py-1 ps-1 pe-3.5 text-sm font-medium text-primary-text shadow-sm transition-colors hover:border-brand-border focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
            >
              <img src="/images/compare/granola.png" alt="Granola" width="24" height="24" class="size-6 rounded-full" />
              {t("marketing.home.hero.pill")}
              <span aria-hidden="true" class="text-secondary-text transition-transform group-hover:translate-x-0.5">
                →
              </span>
            </A>
          }
        >
          {(entry) => (
            <A
              href={entry().href}
              class="group inline-flex items-center gap-2.5 rounded-full border border-primary-border bg-tile py-1 ps-1 pe-3.5 text-sm font-medium text-primary-text shadow-sm transition-colors hover:border-brand-border focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
            >
              <span class="inline-flex h-6 items-center rounded-full bg-brand px-2.5 text-xs font-semibold text-white">
                {t("marketing.home.hero.new_badge")}
              </span>
              {entry().label}
              <span aria-hidden="true" class="text-secondary-text transition-transform group-hover:translate-x-0.5">
                →
              </span>
            </A>
          )}
        </Show>

        {/* Each line of the headline holds on one line from lg up; the size scales so the longer one fits. */}
        <h1 class="mt-7 text-4xl font-semibold tracking-tight text-balance sm:text-6xl lg:text-[clamp(3.25rem,4.6vw,4.5rem)]">
          <span class="block lg:whitespace-nowrap">{headline()[0]}</span>
          <Show when={headline()[1]}>
            {(second) => <span class="block text-brand lg:whitespace-nowrap dark:text-brand-hover">{second()}</span>}
          </Show>
        </h1>
        <p class="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-balance text-secondary-text sm:text-xl">
          {t(audience()?.sub ?? "marketing.home.hero.sub")}
        </p>

        <div class="mt-9 flex flex-wrap items-center justify-center gap-3">
          <DownloadButton from="homepage hero" size="lg" />
          <Show when={hasChiefBuild(os())}>
            <GetStartedButton variant="secondary" size="lg" />
          </Show>
        </div>
        <Show when={audience()}>
          {(entry) => (
            <p class="mt-5">
              <A
                href={entry().page}
                class="group inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline dark:text-brand-hover"
              >
                {t(entry().link)}
                <span aria-hidden="true" class="transition-transform group-hover:translate-x-0.5">
                  →
                </span>
              </A>
            </p>
          )}
        </Show>
        <Show when={announcement}>
          <p class="mt-5">
            <A
              href="/compare"
              class="group inline-flex items-center gap-2 text-sm text-secondary-text transition-colors hover:text-primary-text"
            >
              <img src="/images/compare/granola.png" alt="" width="20" height="20" class="size-5 rounded-full" />
              {t("marketing.home.hero.from_granola")}
              <span aria-hidden="true" class="transition-transform group-hover:translate-x-0.5">
                →
              </span>
            </A>
          </p>
        </Show>
        <Show when={IOS_APP_URL}>
          {(href) => (
            <a
              href={href()}
              class="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-text hover:underline"
            >
              {t("marketing.home.hero.iphone")}
              <span aria-hidden="true">→</span>
            </a>
          )}
        </Show>
      </div>

      <div class="relative mx-auto max-w-6xl">
        <HeroProof />
      </div>

      <div class="relative mx-auto mt-14 max-w-6xl sm:mt-20">
        <HeroVisual />
      </div>
    </section>
  );
};
